import { Controller, useFormContext } from 'react-hook-form';
import { TmdxTextarea } from '@ds';
import { LegTypeEnum, RouteLegTypeEnum, SegmentTypeEnum } from '@/api/apis/route';
import { useWatchFields } from '@/hooks/useWatchFields';
import { MAX_LENGTH_2000 } from '@/constants/form.constants';
import { RouteLegFormMode, useLegFormContext } from '../context/LegFormContext';
import { CommonRouteLegSchemaValues } from '../validation';
import { LegTypeInputs } from './LegTypeInputs';
import { FormResourceList } from './FormResourceList';
import { LegDetailsTitle } from './LegDetailsTitle';
import { AircraftSelect } from './AircraftSelect';
import { CommercialMultiStopBanner, LightsAndSirensBanner } from './banners';
import { MainLocationsInputs } from './locations';
import { SupplementalProviderSelect, TxCenterProviderSelect } from './providers';
import { NonTmdxAviaInputs } from './NonTmdxAviaInputs';
import { NonTmdxCrewMembersInputs } from './NonTmdxCrewMembersInputs';
import { SearchRecommendedFlights } from './SearchRecommendedFlights/SearchRecommendedFlights';
import { CommercialFlightInputs } from './CommercialFlights';
import { GroundLegInputs } from './GroundLegInputs';
import { DeviceDeliveryInputs } from './DeviceDeliveryInputs';
import { AccommodationFormInputs } from './AccommodationFormInputs';
import { GroundTripInfo } from './GroundTripInfo/GroundTripInfo';

export const LegForm = () => {
  const { mode, isOptionalDataEdit } = useLegFormContext();
  const { control } = useFormContext<CommonRouteLegSchemaValues>();
  const { formType, legType, segmentType, tripId } = useWatchFields([
    'formType',
    'legType',
    'segmentType',
    'tripId',
  ] as const);

  const isEditMode = mode === RouteLegFormMode.Edit;

  // ACCOMMODATION LEG
  const isAccommodationForm = legType === RouteLegTypeEnum.AccommodationLeg;

  // AVIA LEGS
  const isTmdxAviaLeg = legType === RouteLegTypeEnum.TmdxJetAviaLeg;
  const isSupplementalLiftLeg = legType === RouteLegTypeEnum.SupplementalLiftAviaLeg;
  const isTxCenterAviaLeg = legType === RouteLegTypeEnum.TxCenterProviderAviaLeg;
  const isCommercialAviaLeg = legType === RouteLegTypeEnum.CommercialFlightAviaLeg;
  const isReposition = [SegmentTypeEnum.reposition, SegmentTypeEnum.returnToBase].includes(segmentType);
  const isNonTmdxAviaInputs = isSupplementalLiftLeg || isTxCenterAviaLeg;

  // GROUND LEGS
  const isGroundForm = formType === LegTypeEnum.ground;
  const isTmdxAffiliateLeg = legType === RouteLegTypeEnum.TmdxAffiliateGroundLeg;
  const isTxCenterGroundLeg = legType === RouteLegTypeEnum.TxCenterProviderGroundLeg;
  const isDeviceDeliveryLeg = legType === RouteLegTypeEnum.CurriServiceGroundLeg;
  const isGroundInputs = isTmdxAffiliateLeg || isTxCenterGroundLeg;

  const isTxCenterProvider = isTxCenterAviaLeg || isTxCenterGroundLeg;
  const isTmdxDriveLegs = [
    RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
    RouteLegTypeEnum.TmdxDriveTedsGroundLeg,
  ].includes(legType);

  const showGroundTripInfo = isTmdxDriveLegs && !!tripId;

  return (
    <div className="flex flex-col gap-4">
      <LegTypeInputs />
      {isSupplementalLiftLeg && <SupplementalProviderSelect />}
      {isTxCenterProvider && <TxCenterProviderSelect />}
      {isGroundForm && <LightsAndSirensBanner />}
      {isEditMode && isCommercialAviaLeg && <CommercialMultiStopBanner />}
      {!isReposition && <FormResourceList />}
      {isCommercialAviaLeg && <SearchRecommendedFlights />}
      {!isAccommodationForm ? <LegDetailsTitle /> : <span className="text-l-sb text-base-5">Leg details</span>}
      {!isAccommodationForm && !isCommercialAviaLeg && <MainLocationsInputs />}
      {isTmdxAviaLeg && <AircraftSelect />}
      {isNonTmdxAviaInputs && (
        <>
          <NonTmdxAviaInputs withBannedTailNumberCheck={isSupplementalLiftLeg} withRoutePrice={isSupplementalLiftLeg} />
          <NonTmdxCrewMembersInputs />
        </>
      )}
      {isCommercialAviaLeg && <CommercialFlightInputs />}
      {isGroundInputs && <GroundLegInputs />}
      {isDeviceDeliveryLeg && <DeviceDeliveryInputs />}
      {isAccommodationForm && <AccommodationFormInputs />}
      <Controller
        name="comments"
        control={control}
        render={({ field, fieldState: { invalid } }) => (
          <TmdxTextarea
            fullWidth
            disableAnimation
            minRows={2}
            aria-label="Comments"
            label="Comments"
            isInvalid={invalid}
            isDisabled={!isOptionalDataEdit}
            className="min-h-16"
            maxLength={MAX_LENGTH_2000}
            {...field}
          />
        )}
      />
      {showGroundTripInfo && <GroundTripInfo tripId={tripId} />}
    </div>
  );
};
